// ink-component
import React, { ThHTMLAttributes } from 'react';
import styled, { ThemeProvider } from 'styled-components';

import CoreCell from '../../_common/CoreCell';
import { styles, cellPresets } from '../../_common/styles';
import { useCachedFrozenCellSettings } from '../../hooks/freeze';
import { NewTableVirtualContext } from '../NewTableVirtualWrapper/NewTableVirtualWrapper';

export type NewTableHeadCellProps = ThHTMLAttributes<HTMLTableCellElement> & {
  /** Align text to left or right */
  align?: 'left' | 'right';
  /** ignore */
  bgColor?: string;
  /** Set the width of the header cell, string with unit (px or %), e.g. '10px' or '10%' */
  width?: string;
  /** Set breaking of the text in multiple lines */
  whiteSpace?: 'normal' | 'nowrap';
  /** Apply special style properties for certain child types inside the cell */
  preset?: 'twiddleIcon' | 'action' | 'checkbox' | 'radio' | 'drag';
  /** Freezes the cell to the left when the table is scrolled. You should **always**
   * pass this prop to the **header and all the cells in the same column**.
   * If you want to freeze **more than one column** in a table, make sure you freeze them
   * **in a sequence**. From the first column to the last one you want to freeze,
   * without skipping any cell or header in between. */
  freeze?: boolean | React.CSSProperties['left'];
  /** Adds a dark blue border to the left or right side */
  divider?: 'left' | 'right';
};

/* @internal */
export const StyledHeader = styled(CoreCell)<NewTableHeadCellProps>`
  ${styles['header']}
  ${({ theme }) => cellPresets[theme.cellPreset]}
`;

/**
 * @group NewTable
 * @purpose Header cell for column title
 * @categories table
 * @HTMLType HTMLTableHeaderCellElement
 */
const NewTableHeadCell = (props: NewTableHeadCellProps) => {
  const { align, bgColor, className, divider, freeze, preset, style, whiteSpace, width, ...otherProps } = props;

  const { offsetLeft, lastFrozenColumn, cellRef } = useCachedFrozenCellSettings();

  const virtual = React.useContext(NewTableVirtualContext);

  /*
    Since all head presets also add a width (see NewTable/_common/styles.ts),
    we need to require either a width or a preset.
  */
  if (virtual && !(width || preset)) {
    throw new Error(
      'NewTable.HeadCell: `width` or `preset` prop are required when using virtualization to avoid content shifting.',
    );
  }

  return (
    <ThemeProvider
      theme={rowTheme => ({
        ...rowTheme,
        cellAlign: align,
        cellBgColor: bgColor,
        cellPreset: preset,
        cellDivider: divider,
        cellFreeze: freeze,
        cellLastFrozenColumn: lastFrozenColumn,
        cellOffsetLeft: typeof freeze === 'boolean' ? `${offsetLeft}px` : freeze,
        cellWhiteSpace: whiteSpace,
        cellWidthValue: props.width,
      })}
    >
      <StyledHeader as="th" data-freeze={freeze ? '' : undefined} {...otherProps} ref={cellRef} />
    </ThemeProvider>
  );
};

NewTableHeadCell.defaultProps = {
  align: 'left',
  bgColor: 'gray-lightest-2', // HeadCell colors should not allow
  divider: undefined,
  freeze: undefined,
  preset: undefined,
  whiteSpace: 'nowrap',
  width: undefined,
};

NewTableHeadCell.displayName = 'NewTable.HeadCell';

export default NewTableHeadCell;
